import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const services = [
    'Malerarbeiten',
    'Stucco Veneziano',
    'Tapezieren',
    'Gipserarbeiten',
    'Schalungsarbeiten',
    'Trockenbau',
    'Fassaden',
    'Streichen & Putzen',
    'Schimmel & Rissreparatur',
    'Pflaster & Dachreinigung',
    'Sonstiges'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', phone: '', email: '', service: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-[#003B7A] text-white" data-testid="contact-hero">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl"
          >
            <p className="font-body text-xs tracking-[0.3em] uppercase text-[#FFD700] mb-6" data-testid="contact-subtitle">
              Kontakt
            </p>
            <h1 className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight leading-[1.1] mb-8" data-testid="contact-title">
              LASSEN SIE UNS
              <span className="block gold-shimmer mt-2">ÜBER IHR PROJEKT SPRECHEN</span>
            </h1>
            <p className="font-body text-lg md:text-xl leading-relaxed text-gray-300" data-testid="contact-description">
              Kostenlose Beratung und Angebot innerhalb von 24 Stunden. Wir freuen uns auf Ihre Anfrage!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24 bg-white" data-testid="contact-content">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8" data-testid="contact-info-title">
                Kontaktinformationen
              </h2>
              
              <div className="space-y-8 mb-12">
                <div className="flex items-start gap-4" data-testid="contact-address">
                  <MapPin className="w-6 h-6 text-[#FFD700] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-lg mb-2">Adresse</h3>
                    <p className="font-body text-base text-gray-700">
                      Schellenberg 5<br />
                      7408 Cazis<br />
                      Schweiz
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-phone">
                  <Phone className="w-6 h-6 text-[#FFD700] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-lg mb-2">Telefon</h3>
                    <a 
                      href="tel:+41792649063"
                      className="font-body text-base text-gray-700 hover:text-[#FFD700] transition-colors"
                    >
                      +41 79 264 90 63
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-hours">
                  <Clock className="w-6 h-6 text-[#FFD700] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-lg mb-2">Öffnungszeiten</h3>
                    <p className="font-body text-base text-gray-700">
                      Montag - Freitag: Bis 17:00 Uhr
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <a
                  href="https://wa.me/41792649063"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full bg-[#25D366] text-white px-8 py-4 text-sm font-bold tracking-widest uppercase hover:bg-[#1ea952] transition-all duration-300"
                  data-testid="contact-whatsapp-button"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp Chat starten
                </a>
                <a
                  href="tel:+41792649063"
                  className="flex items-center justify-center gap-2 w-full bg-[#003B7A] text-white px-8 py-4 text-sm font-bold tracking-widest uppercase hover:bg-[#1a1a1a] transition-all duration-300"
                  data-testid="contact-call-button"
                >
                  <Phone className="w-5 h-5" />
                  Jetzt anrufen
                </a>
              </div>

              {/* Map with correct coordinates for Cazis */}
              <div className="mt-12" data-testid="contact-map">
                <h3 className="font-heading font-semibold text-lg mb-4">Standort</h3>
                <div className="w-full h-[300px] bg-gray-200 rounded-sm overflow-hidden shadow-lg">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2733.8!2d9.429722!3d46.719444!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4784c3e8b8b8b8b8%3A0x0!2zNDbCsDQzJzEwLjAiTiA5wrAyNSc0Ny4wIkU!5e0!3m2!1sde!2sch!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Marali Immobilien AG Standort - Schellenberg 5, 7408 Cazis"
                  ></iframe>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-[#F5F5F7] p-8 md:p-12">
                <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6" data-testid="contact-form-title">
                  Angebot anfordern
                </h2>
                <p className="font-body text-base text-gray-700 mb-8">
                  Füllen Sie das Formular aus und wir melden uns innerhalb von 24 Stunden bei Ihnen.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label htmlFor="name" className="block font-body font-semibold text-sm mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 focus:border-[#FFD700] focus:outline-none transition-colors font-body"
                      placeholder="Ihr Name"
                      data-testid="contact-form-name"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block font-body font-semibold text-sm mb-2">
                      Telefon *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 focus:border-[#FFD700] focus:outline-none transition-colors font-body"
                      placeholder="+41 79 123 45 67"
                      data-testid="contact-form-phone"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block font-body font-semibold text-sm mb-2">
                      E-Mail (optional)
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 focus:border-[#FFD700] focus:outline-none transition-colors font-body"
                      placeholder="ihre.email@beispiel.ch"
                      data-testid="contact-form-email"
                    />
                  </div>

                  <div>
                    <label htmlFor="service" className="block font-body font-semibold text-sm mb-2">
                      Gewünschte Leistung
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 focus:border-[#FFD700] focus:outline-none transition-colors font-body"
                      data-testid="contact-form-service"
                    >
                      <option value="">Bitte wählen...</option>
                      {services.map((service) => (
                        <option key={service} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block font-body font-semibold text-sm mb-2">
                      Nachricht *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-3 bg-white border border-gray-300 focus:border-[#FFD700] focus:outline-none transition-colors font-body resize-none"
                      placeholder="Beschreiben Sie Ihr Projekt..."
                      data-testid="contact-form-message"
                    ></textarea>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3" data-testid="contact-form-success">
                      <p className="font-body text-sm">Vielen Dank! Wir melden uns innerhalb von 24 Stunden bei Ihnen.</p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3" data-testid="contact-form-error">
                      <p className="font-body text-sm">Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut oder rufen Sie uns an.</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#FFD700] text-white px-8 py-4 text-sm font-bold tracking-widest uppercase hover:bg-[#c19d2f] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="contact-form-submit"
                  >
                    {isSubmitting ? (
                      'Wird gesendet...'
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Angebot anfordern
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-[#F5F5F7]" data-testid="faq-section">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mb-6" data-testid="faq-title">
              Häufig gestellte Fragen
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                question: 'Wie hoch sind die Kosten?',
                answer: 'Die Kosten variieren je nach Projektumfang. Wir erstellen Ihnen gerne ein kostenloses und unverbindliches Angebot nach einer Besichtigung vor Ort.'
              },
              {
                question: 'Wie lange dauert ein Projekt?',
                answer: 'Die Dauer hängt vom Umfang ab. Nach der Besichtigung können wir Ihnen einen genauen Zeitplan nennen. Wir arbeiten effizient und halten vereinbarte Termine ein.'
              },
              {
                question: 'Wann können Sie starten?',
                answer: 'Wir bemühen uns, schnellstmöglich zu starten. Nach Auftragserteilung können wir meist innerhalb von 1-2 Wochen beginnen.'
              },
              {
                question: 'Ist die Beratung wirklich kostenlos?',
                answer: 'Ja, die erste Beratung und Besichtigung vor Ort ist vollkommen kostenlos und unverbindlich. Sie erhalten danach ein transparentes Angebot.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8"
                data-testid={`faq-item-${index}`}
              >
                <h3 className="font-heading font-bold text-xl mb-4">{faq.question}</h3>
                <p className="font-body text-base text-gray-700 leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;